/**
 * Which chat messages count as "history" for the custom-theme signals.
 *
 * Both theme signals must fire for fresh agent replies only: a `created_custom_theme` card
 * live-previews itself on mount (flipping the dashboard's theme), and a `theme_request_form`
 * marker reopens the request form. Replaying either off an old message when chat is reopened is
 * a visible bug, so freshness is decided against a snapshot of the thread's loaded history.
 *
 * The snapshot may only be taken once BOTH loads have settled — resolving which thread to use
 * (isLoadingExistingThreads) and fetching that thread's messages (isLoading) are separate
 * queries. Snapshotting between them captures an empty message list, after which every
 * historical message looks new.
 */

export interface ThemeHistorySnapshot {
  threadId: string | null;
  ids: Set<string>;
}

interface SnapshotInput {
  snapshot: ThemeHistorySnapshot | null;
  threadId: string | null;
  messageIds: string[];
  /** The thread's messages are still being fetched. */
  isLoading: boolean;
  /** Which thread to use is still being resolved. */
  isLoadingExistingThreads: boolean;
}

/**
 * The snapshot to store, or null to keep the current one.
 *
 * Returns a new snapshot when there is none yet or the thread changed (switching threads
 * mid-session must not carry the previous thread's ids), and null while either load is in
 * flight — the caller keeps waiting, treating everything as history until then.
 */
export function nextThemeHistorySnapshot({
  snapshot,
  threadId,
  messageIds,
  isLoading,
  isLoadingExistingThreads,
}: SnapshotInput): ThemeHistorySnapshot | null {
  if (snapshot && snapshot.threadId === threadId) {
    return null;
  }
  if (isLoading || isLoadingExistingThreads) {
    return null;
  }
  return { threadId, ids: new Set(messageIds) };
}

/**
 * Whether a message arrived after the current thread's history was snapshotted.
 *
 * `threadId` is required, not decorative: after a thread switch the previous thread's snapshot is
 * still held until the new thread's messages load, and its ids say nothing about the new thread —
 * trusting it would make that thread's existing cards look fresh.
 */
export function isFreshThemeMessage(
  snapshot: ThemeHistorySnapshot | null,
  messageId: string,
  threadId: string | null
): boolean {
  // No usable snapshot means history is still settling — we cannot tell, so assume history.
  if (!snapshot || snapshot.threadId !== threadId) {
    return false;
  }
  return !snapshot.ids.has(messageId);
}
